/**
 * Demo 2.0 — the identified demo-lead session on the FE side.
 *
 * After the email→OTP gate (or an `?invite=` link) the backend returns a
 * signed, stateless lead token. It lives in sessionStorage — tab-scoped and
 * low-sensitivity (it only unlocks the simulated demo + attributes events) —
 * so `/demo-new/*` deep links and reloads survive without re-gating.
 * NOT localStorage on purpose, and never a Bearer header (the backend's global
 * seller guard probes those): calls send it as `X-Demo-Token` / in the body.
 */
const LEAD_KEY = 'tara_demo_lead';
const SESSION_KEY = 'tara_demo_lead_session';

export interface DemoLeadSession {
  token: string;
  email: string;
  name?: string | null;
  /** epoch ms */
  expires_at: number;
}

const safeSession = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const getDemoLead = (): DemoLeadSession | null => {
  try {
    const raw = safeSession()?.getItem(LEAD_KEY);
    if (!raw) return null;
    const lead = JSON.parse(raw) as DemoLeadSession;
    if (!lead?.token || !lead?.expires_at) return null;
    if (Date.now() >= lead.expires_at) {
      safeSession()?.removeItem(LEAD_KEY);
      return null;
    }
    return lead;
  } catch {
    return null;
  }
};

export const setDemoLead = (lead: DemoLeadSession): void => {
  try {
    safeSession()?.setItem(LEAD_KEY, JSON.stringify(lead));
  } catch {
    /* storage unavailable — the gate will just re-ask next load */
  }
};

export const clearDemoLead = (): void => {
  safeSession()?.removeItem(LEAD_KEY);
};

/**
 * Sign Out inside the demo: forget the identified lead and reload the demo
 * entry, which shows the gate again.
 */
export const signOutOfDemo = (
  go: (url: string) => void = (url) => window.location.assign(url)
): void => {
  try {
    clearDemoLead();
  } catch {
    /* storage unavailable: nothing was stored either */
  }
  go('/demo-new');
};

/* ---- ?invite= capture ----
 * The app's internal redirects (/ → /login → /dashboard) strip the query
 * before the lazy demo shell mounts, so the invite token must be captured at
 * BOOTSTRAP (index.tsx) and consumed by the gate afterwards. */
const INVITE_KEY = 'tara_demo_invite_token';

export const captureInviteToken = (): void => {
  try {
    const token = new URLSearchParams(window.location.search).get('invite');
    if (token) safeSession()?.setItem(INVITE_KEY, token);
  } catch {
    /* ignore */
  }
};

/** One-shot read of a captured invite token. */
export const consumeInviteToken = (): string | null => {
  const s = safeSession();
  const token = s?.getItem(INVITE_KEY) || null;
  if (token) s?.removeItem(INVITE_KEY);
  return token;
};

/* ---- campaign attribution capture ----
 * Same reason as the invite token: `/demo-new?utm_...` is redirected to
 * `/demo-new/dashboard` and the query is gone before the gate or the event
 * tracker can read it. Captured at BOOTSTRAP, first touch wins. */
const ATTRIBUTION_KEY = 'tara_demo_attribution';
const ATTRIBUTION_QUERY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
] as const;
/** The API stores at most this many characters per value. */
const ATTRIBUTION_MAX_LENGTH = 255;

export type DemoAttributionKey =
  | typeof ATTRIBUTION_QUERY_KEYS[number]
  | 'referrer';
export type DemoAttribution = Partial<Record<DemoAttributionKey, string>>;

const ATTRIBUTION_KEYS: readonly DemoAttributionKey[] = [
  ...ATTRIBUTION_QUERY_KEYS,
  'referrer',
];

/** The referrer, only when the visitor came from another origin. */
const externalReferrer = (): string => {
  const referrer = document.referrer;
  if (!referrer) return '';
  try {
    return new URL(referrer).origin === window.location.origin ? '' : referrer;
  } catch {
    return '';
  }
};

/**
 * Store the first-touch campaign values of this tab. A stored snapshot is
 * never overwritten or cleared by a later load; a load without any campaign
 * value and without an external referrer stores nothing.
 */
export const captureDemoAttribution = (): void => {
  try {
    const s = safeSession();
    if (!s || s.getItem(ATTRIBUTION_KEY)) return;
    const out: DemoAttribution = {};
    const params = new URLSearchParams(window.location.search);
    ATTRIBUTION_QUERY_KEYS.forEach((key) => {
      const value = (params.get(key) || '').trim();
      if (value) out[key] = value.slice(0, ATTRIBUTION_MAX_LENGTH);
    });
    const referrer = externalReferrer();
    if (referrer) out.referrer = referrer.slice(0, ATTRIBUTION_MAX_LENGTH);
    if (Object.keys(out).length === 0) return;
    s.setItem(ATTRIBUTION_KEY, JSON.stringify(out));
  } catch {
    /* ignore */
  }
};

/** The captured first-touch values, or an empty object. */
export const getDemoAttribution = (): DemoAttribution => {
  try {
    const raw = safeSession()?.getItem(ATTRIBUTION_KEY);
    if (!raw) return {};
    const stored = JSON.parse(raw) as Record<string, unknown> | null;
    if (!stored || typeof stored !== 'object') return {};
    const out: DemoAttribution = {};
    ATTRIBUTION_KEYS.forEach((key) => {
      const value = stored[key];
      if (typeof value === 'string' && value) out[key] = value;
    });
    return out;
  } catch {
    return {};
  }
};

/** Stable per-tab session uuid, minted once — groups events into sessions. */
export const getDemoSessionId = (): string => {
  const s = safeSession();
  const existing = s?.getItem(SESSION_KEY);
  if (existing) return existing;
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    s?.setItem(SESSION_KEY, id);
  } catch {
    /* fall back to the in-memory value for this load */
  }
  return id;
};
